/**
 * Claude Code SDK JSON Lines Protocol Types
 *
 * This module defines the wire protocol types for bidirectional communication
 * with Claude Code via JSON Lines over stdin/stdout.
 *
 * @see CLAUDE_PROTOCOL.md for full specification
 */

import { z } from 'zod';

// =============================================================================
// Content Blocks
// =============================================================================

export const TextContentBlockSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});
export type TextContentBlock = z.infer<typeof TextContentBlockSchema>;

export const ThinkingContentBlockSchema = z.object({
  type: z.literal('thinking'),
  thinking: z.string(),
  signature: z.string().optional(),
});
export type ThinkingContentBlock = z.infer<typeof ThinkingContentBlockSchema>;

export const ToolUseContentBlockSchema = z.object({
  type: z.literal('tool_use'),
  id: z.string(),
  name: z.string(),
  input: z.unknown(),
});
export type ToolUseContentBlock = z.infer<typeof ToolUseContentBlockSchema>;

export const ToolResultContentBlockSchema = z.object({
  type: z.literal('tool_result'),
  tool_use_id: z.string(),
  content: z.union([z.string(), z.array(z.unknown())]).optional(),
  is_error: z.boolean().optional(),
});
export type ToolResultContentBlock = z.infer<typeof ToolResultContentBlockSchema>;

export const ImageSourceSchema = z.object({
  type: z.literal('base64'),
  media_type: z.string(),
  data: z.string(),
});
export type ImageSource = z.infer<typeof ImageSourceSchema>;

export const ImageContentBlockSchema = z.object({
  type: z.literal('image'),
  source: ImageSourceSchema,
});
export type ImageContentBlock = z.infer<typeof ImageContentBlockSchema>;

export const ContentBlockSchema = z.discriminatedUnion('type', [
  TextContentBlockSchema,
  ThinkingContentBlockSchema,
  ToolUseContentBlockSchema,
  ToolResultContentBlockSchema,
  ImageContentBlockSchema,
]);
export type ContentBlock = z.infer<typeof ContentBlockSchema>;

// =============================================================================
// Client → Claude Messages
// =============================================================================

/**
 * User message sent to Claude via query()
 */
export const UserMessageSchema = z.object({
  type: z.literal('user'),
  message: z.object({
    role: z.literal('user'),
    content: z.array(ContentBlockSchema),
  }),
  session_id: z.string().optional(),
});
export type UserMessage = z.infer<typeof UserMessageSchema>;

/**
 * Interrupt request to stop current response
 */
export const InterruptRequestInnerSchema = z.object({
  subtype: z.literal('interrupt'),
});
export type InterruptRequestInner = z.infer<typeof InterruptRequestInnerSchema>;

/**
 * Ask the running CLI which MCP servers its session currently holds. The
 * reply lists every server with its connection status and configuration
 * scope — `dynamic` for servers added over the control channel, a settings
 * scope (`user`, `project`, `local`, ...) for servers read from config at
 * startup.
 */
export const McpStatusRequestInnerSchema = z.object({
  subtype: z.literal('mcp_status'),
});
export type McpStatusRequestInner = z.infer<typeof McpStatusRequestInnerSchema>;

/**
 * Replace the set of MCP servers the running CLI holds *dynamically* — the
 * only way to give a live session a server it did not read from config at
 * startup. Servers configured through settings files are untouched, and the
 * previous dynamic set is replaced wholesale, so every send must carry the
 * full set the session should end up with.
 *
 * `servers` is the CLI's own config map (`{ [name]: { type, url, headers } }`
 * for remote transports), kept as `unknown` values here because the exact
 * per-transport shape is the CLI's contract, not ours.
 */
export const McpSetServersRequestInnerSchema = z.object({
  subtype: z.literal('mcp_set_servers'),
  servers: z.record(z.string(), z.unknown()),
});
export type McpSetServersRequestInner = z.infer<typeof McpSetServersRequestInnerSchema>;

/** Switch the CLI's model for its later responses. */
export const SetModelRequestInnerSchema = z.object({
  subtype: z.literal('set_model'),
  model: z.string().min(1),
});
export type SetModelRequestInner = z.infer<typeof SetModelRequestInnerSchema>;

/** Control requests the client may send to a running Claude Code CLI. */
export const ClientControlRequestInnerSchema = z.discriminatedUnion('subtype', [
  InterruptRequestInnerSchema,
  McpStatusRequestInnerSchema,
  McpSetServersRequestInnerSchema,
  SetModelRequestInnerSchema,
]);
export type ClientControlRequestInner = z.infer<typeof ClientControlRequestInnerSchema>;

/**
 * Control request from client to Claude.
 */
export const ClientControlRequestSchema = z.object({
  type: z.literal('control_request'),
  request_id: z.string(),
  request: ClientControlRequestInnerSchema,
});
export type ClientControlRequest = z.infer<typeof ClientControlRequestSchema>;

/**
 * Claude's reply to a client-issued {@link ClientControlRequestSchema}.
 * Distinct from {@link ServerControlResponseSchema}, which is the client's
 * acknowledgment of a request the *CLI* raised: this one carries the
 * subtype's result payload, or an error string when the CLI refused.
 */
export const ClientControlReplySchema = z.object({
  type: z.literal('control_response'),
  response: z.discriminatedUnion('subtype', [
    z.object({
      subtype: z.literal('success'),
      request_id: z.string(),
      response: z.unknown().optional(),
    }),
    z.object({
      subtype: z.literal('error'),
      request_id: z.string(),
      error: z.string(),
    }),
  ]),
});
export type ClientControlReply = z.infer<typeof ClientControlReplySchema>;

/**
 * Permission rule for tool access
 */
export const PermissionRuleSchema = z.object({
  toolName: z.string(),
});
export type PermissionRule = z.infer<typeof PermissionRuleSchema>;

/**
 * Permission update to add rules
 */
export const PermissionUpdateSchema = z.object({
  type: z.literal('addRules'),
  rules: z.array(PermissionRuleSchema),
  behavior: z.enum(['allow', 'deny']),
  destination: z.enum(['session', 'project', 'global']),
});
export type PermissionUpdate = z.infer<typeof PermissionUpdateSchema>;

/**
 * Allow response for tool use
 */
export const AllowResponseSchema = z.object({
  behavior: z.literal('allow'),
  toolUseID: z.string(),
  updatedInput: z.unknown().optional(),
  updatedPermissions: z.array(PermissionUpdateSchema).optional(),
});
export type AllowResponse = z.infer<typeof AllowResponseSchema>;

/**
 * Deny response for tool use
 */
export const DenyResponseSchema = z.object({
  behavior: z.literal('deny'),
  toolUseID: z.string(),
  message: z.string(),
  interrupt: z.boolean().optional(),
});
export type DenyResponse = z.infer<typeof DenyResponseSchema>;

/**
 * Tool use response (allow or deny)
 */
export const ToolUseResponseSchema = z.union([AllowResponseSchema, DenyResponseSchema]);
export type ToolUseResponse = z.infer<typeof ToolUseResponseSchema>;

/**
 * Control response from client to Claude
 */
export const ClientControlResponseSchema = z.object({
  type: z.literal('control_response'),
  response: z.object({
    subtype: z.literal('success'),
    request_id: z.string(),
    response: ToolUseResponseSchema.optional(),
  }),
});
export type ClientControlResponse = z.infer<typeof ClientControlResponseSchema>;

// =============================================================================
// Claude → Client Messages
// =============================================================================

// --- System Messages ---

export const SystemInitSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('init'),
  session_id: z.string(),
  cwd: z.string().optional(),
  model: z.string().optional(),
  tools: z.array(z.string()).optional(),
  mcp_servers: z.array(z.unknown()).optional(),
  slash_commands: z.array(z.string()).optional(),
  agents: z.array(z.string()).optional(),
  plugins: z.array(z.unknown()).optional(),
  skills: z.array(z.unknown()).optional(),
  claude_code_version: z.string().optional(),
  apiKeySource: z.string().optional(),
  output_style: z.string().optional(),
  permissionMode: z.string().optional(),
  fast_mode_state: z.string().optional(),
});
export type SystemInit = z.infer<typeof SystemInitSchema>;

export const SystemStatusSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('status'),
  session_id: z.string(),
  status: z.string(),
  uuid: z.string().optional(),
});
export type SystemStatus = z.infer<typeof SystemStatusSchema>;

export const SystemCompactBoundarySchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('compact_boundary'),
  session_id: z.string(),
  compact_metadata: z.unknown().optional(),
  uuid: z.string().optional(),
});
export type SystemCompactBoundary = z.infer<typeof SystemCompactBoundarySchema>;

export const SystemTaskStartedSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('task_started'),
  session_id: z.string(),
  task_id: z.string(),
  task_type: z.enum(['local_agent', 'local_bash']),
  tool_use_id: z.string().optional(),
  description: z.string().optional(),
  uuid: z.string().optional(),
});
export type SystemTaskStarted = z.infer<typeof SystemTaskStartedSchema>;

export const SystemTaskProgressSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('task_progress'),
  session_id: z.string(),
  task_id: z.string(),
  tool_use_id: z.string().optional(),
  description: z.string().optional(),
  last_tool_name: z.string().optional(),
  usage: z.record(z.string(), z.unknown()).optional(),
  uuid: z.string().optional(),
});
export type SystemTaskProgress = z.infer<typeof SystemTaskProgressSchema>;

export const SystemTaskNotificationSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('task_notification'),
  session_id: z.string(),
  task_id: z.string(),
  tool_use_id: z.string().optional(),
  status: z.enum(['completed', 'failed', 'stopped']),
  summary: z.string().optional(),
  output_file: z.string().optional(),
  usage: z.record(z.string(), z.unknown()).optional(),
  uuid: z.string().optional(),
});
export type SystemTaskNotification = z.infer<typeof SystemTaskNotificationSchema>;

export const SystemTaskUpdatedSchema = z.object({
  type: z.literal('system'),
  subtype: z.literal('task_updated'),
  session_id: z.string(),
  task_id: z.string(),
  patch: z.object({
    end_time: z.number().optional(),
    status: z.enum(['completed', 'failed', 'stopped']).optional(),
  }),
  uuid: z.string().optional(),
});
export type SystemTaskUpdated = z.infer<typeof SystemTaskUpdatedSchema>;

export const SystemMessageSchema = z.discriminatedUnion('subtype', [
  SystemInitSchema,
  SystemStatusSchema,
  SystemCompactBoundarySchema,
  SystemTaskStartedSchema,
  SystemTaskProgressSchema,
  SystemTaskNotificationSchema,
  SystemTaskUpdatedSchema,
]);
export type SystemMessage = z.infer<typeof SystemMessageSchema>;

// --- User Echo ---

export const UserEchoSchema = z.object({
  type: z.literal('user'),
  message: z.object({
    role: z.literal('user'),
    content: z.array(z.unknown()),
  }),
  session_id: z.string(),
});
export type UserEcho = z.infer<typeof UserEchoSchema>;

// --- Assistant Message ---

export const AssistantUsageSchema = z.object({
  input_tokens: z.number().optional(),
  output_tokens: z.number().optional(),
  cache_creation_input_tokens: z.number().optional(),
  cache_read_input_tokens: z.number().optional(),
  service_tier: z.string().optional(),
  cache_creation: z
    .object({
      ephemeral_1h_input_tokens: z.number().optional(),
      ephemeral_5m_input_tokens: z.number().optional(),
    })
    .optional(),
});
export type AssistantUsage = z.infer<typeof AssistantUsageSchema>;

export const AssistantMessageSchema = z.object({
  type: z.literal('assistant'),
  message: z.object({
    id: z.string(),
    role: z.literal('assistant'),
    model: z.string().optional(),
    content: z.array(ContentBlockSchema),
    stop_reason: z.enum(['end_turn', 'tool_use', 'max_tokens']).nullable().optional(),
    stop_sequence: z.string().nullable().optional(),
    usage: AssistantUsageSchema.optional(),
  }),
  session_id: z.string(),
  uuid: z.string().optional(),
  parent_tool_use_id: z.string().nullable().optional(),
});
export type AssistantMessage = z.infer<typeof AssistantMessageSchema>;

/**
 * Whether a Claude frame came from a Task subagent rather than the main
 * thread. Subagent frames interleave into the same stream marked by a non-null
 * `parent_tool_use_id`; an absent field means main thread.
 *
 * A subagent speaks only for itself: it runs whatever model its agent
 * definition pins — usually a smaller one — and reports its own per-request
 * token usage. So anything that describes *the session* (the window a context
 * meter sizes against, the model a surface names) has to skip these.
 *
 * Accepts both assistant shapes on the wire, {@link AssistantMessage} and
 * {@link TurnClaudeAssistantPayload}, plus the loosely-typed payload records
 * the stream readers carry.
 */
export function isSubagentFrame(frame: { parent_tool_use_id?: unknown }): boolean {
  return frame.parent_tool_use_id != null;
}

/**
 * Placeholder `model` values the CLI writes on messages no model produced.
 * Claude Code stamps locally-generated assistant messages — an interrupt
 * notice, an API-error bubble, a queued-command echo — with `<synthetic>`, and
 * the angle brackets are the CLI's marker for "not a model id". Matching the
 * shape rather than the one literal keeps a future sentinel out of consumers
 * too.
 */
const SENTINEL_MODEL_PATTERN = /^<.*>$/;

/**
 * Whether a `model` value is a CLI placeholder rather than a model id, and so
 * must never be shown as the model that answered. A real id that merely
 * carries a bracketed suffix (`claude-opus-5[1m]`) is unaffected.
 */
export function isSentinelModelId(model: string): boolean {
  return SENTINEL_MODEL_PATTERN.test(model);
}

// --- Result ---

export const ResultSuccessSchema = z.object({
  type: z.literal('result'),
  subtype: z.literal('success'),
  is_error: z.literal(false),
  duration_ms: z.number(),
  duration_api_ms: z.number(),
  num_turns: z.number(),
  session_id: z.string(),
  total_cost_usd: z.number(),
  uuid: z.string().optional(),
  permission_denials: z.array(z.unknown()).optional(),
});
export type ResultSuccess = z.infer<typeof ResultSuccessSchema>;

export const ResultErrorSchema = z.object({
  type: z.literal('result'),
  subtype: z.enum(['error_max_turns', 'error_during_execution']),
  is_error: z.literal(true),
  duration_ms: z.number(),
  duration_api_ms: z.number(),
  num_turns: z.number(),
  session_id: z.string(),
  total_cost_usd: z.number(),
  errors: z.array(z.string()).optional(),
  uuid: z.string().optional(),
  permission_denials: z.array(z.unknown()).optional(),
});
export type ResultError = z.infer<typeof ResultErrorSchema>;

export const ResultMessageSchema = z.union([ResultSuccessSchema, ResultErrorSchema]);
export type ResultMessage = z.infer<typeof ResultMessageSchema>;

// --- Error ---

export const ErrorMessageSchema = z.object({
  type: z.literal('error'),
  error: z.object({
    type: z.string(),
    message: z.string(),
  }),
  request_id: z.string().nullable().optional(),
  session_id: z.string().optional(),
});
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;

// --- Rate Limit Event ---

export const RateLimitInfoSchema = z.object({
  status: z.enum(['allowed', 'allowed_warning', 'rejected']),
  resetsAt: z.number().nullable().optional(),
  rateLimitType: z.string().optional(),
  utilization: z.number().optional(),
  overageStatus: z.string().optional(),
  overageDisabledReason: z.string().optional(),
  isUsingOverage: z.boolean(),
});
export type RateLimitInfo = z.infer<typeof RateLimitInfoSchema>;

export const RateLimitEventSchema = z.object({
  type: z.literal('rate_limit_event'),
  session_id: z.string(),
  uuid: z.string().optional(),
  rate_limit_info: RateLimitInfoSchema,
});
export type RateLimitEvent = z.infer<typeof RateLimitEventSchema>;

// --- Control Request (Claude → Client) ---

export const CanUseToolRequestSchema = z.object({
  subtype: z.literal('can_use_tool'),
  tool_name: z.string(),
  tool_use_id: z.string(),
  input: z.unknown().optional(),
});
export type CanUseToolRequest = z.infer<typeof CanUseToolRequestSchema>;

export const HookCallbackRequestSchema = z.object({
  subtype: z.literal('hook_callback'),
  hook_name: z.string(),
  hook_data: z.unknown(),
});
export type HookCallbackRequest = z.infer<typeof HookCallbackRequestSchema>;

export const McpMessageRequestSchema = z.object({
  subtype: z.literal('mcp_message'),
  server_name: z.string(),
  message: z.unknown(),
});
export type McpMessageRequest = z.infer<typeof McpMessageRequestSchema>;

export const SdkControlInterruptRequestSchema = z.object({
  subtype: z.literal('sdk_control_interrupt'),
});
export type SdkControlInterruptRequest = z.infer<typeof SdkControlInterruptRequestSchema>;

export const ServerControlRequestInnerSchema = z.discriminatedUnion('subtype', [
  CanUseToolRequestSchema,
  HookCallbackRequestSchema,
  McpMessageRequestSchema,
  SdkControlInterruptRequestSchema,
]);
export type ServerControlRequestInner = z.infer<typeof ServerControlRequestInnerSchema>;

export const ServerControlRequestSchema = z.object({
  type: z.literal('control_request'),
  request_id: z.string(),
  request: ServerControlRequestInnerSchema,
});
export type ServerControlRequest = z.infer<typeof ServerControlRequestSchema>;

// --- Control Response (Claude → Client acknowledgment) ---

export const ServerControlResponseSchema = z.object({
  type: z.literal('control_response'),
  response: z.object({
    subtype: z.literal('success'),
    request_id: z.string(),
  }),
});
export type ServerControlResponse = z.infer<typeof ServerControlResponseSchema>;

// =============================================================================
// Combined Output Types
// =============================================================================

/**
 * All possible message types from Claude → Client
 */
export const ClaudeOutputSchema = z.discriminatedUnion('type', [
  SystemInitSchema,
  SystemStatusSchema,
  SystemCompactBoundarySchema,
  SystemTaskStartedSchema,
  SystemTaskProgressSchema,
  SystemTaskNotificationSchema,
  UserEchoSchema,
  AssistantMessageSchema,
  ResultSuccessSchema,
  ResultErrorSchema,
  ErrorMessageSchema,
  RateLimitEventSchema,
  ServerControlRequestSchema,
  ServerControlResponseSchema,
]);
export type ClaudeOutput = z.infer<typeof ClaudeOutputSchema>;

/**
 * All possible message types from Client → Claude
 */
export const ClaudeInputSchema = z.union([
  UserMessageSchema,
  ClientControlRequestSchema,
  ClientControlResponseSchema,
]);
export type ClaudeInput = z.infer<typeof ClaudeInputSchema>;

// =============================================================================
// Backward-Compatible Aliases (TurnClaude* naming)
// =============================================================================
// These match the looser schemas previously in shared/src/messages.ts
// for backward compatibility with existing code.

export const TurnClaudeContentBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({
    type: z.literal('thinking'),
    thinking: z.string(),
    signature: z.string().optional(),
  }),
  z.object({ type: z.literal('tool_use'), id: z.string(), name: z.string(), input: z.unknown() }),
  z.object({
    type: z.literal('tool_result'),
    tool_use_id: z.string(),
    content: z.union([z.string(), z.array(z.unknown())]).optional(),
    is_error: z.boolean().optional(),
  }),
  z.object({ type: z.literal('image'), source: z.record(z.string(), z.unknown()) }),
]);
export type TurnClaudeContentBlock = z.infer<typeof TurnClaudeContentBlockSchema>;

const TurnClaudeMessageSchema = z.object({
  id: z.string(),
  role: z.literal('assistant'),
  model: z.string().optional(),
  content: z.array(TurnClaudeContentBlockSchema),
  stop_reason: z.enum(['end_turn', 'tool_use', 'max_tokens']).nullable().optional(),
  stop_sequence: z.string().nullable().optional(),
  usage: z.record(z.string(), z.unknown()).optional(),
});

export const TurnClaudeAssistantPayloadSchema = z.object({
  type: z.literal('assistant'),
  message: TurnClaudeMessageSchema,
  session_id: z.string(),
  uuid: z.string().optional(),
  parent_tool_use_id: z.string().nullable().optional(),
});
export type TurnClaudeAssistantPayload = z.infer<typeof TurnClaudeAssistantPayloadSchema>;

export const TurnClaudeResultPayloadSchema = z.object({
  type: z.literal('result'),
  subtype: z.enum(['success', 'error_max_turns', 'error_during_execution']),
  is_error: z.boolean(),
  result: z.string().nullable().optional(),
  errors: z.array(z.string()).optional(),
  session_id: z.string(),
  duration_ms: z.number().optional(),
  duration_api_ms: z.number().optional(),
  total_cost_usd: z.number().optional(),
  num_turns: z.number().optional(),
  usage: z.record(z.string(), z.unknown()).optional(),
  uuid: z.string().optional(),
});
export type TurnClaudeResultPayload = z.infer<typeof TurnClaudeResultPayloadSchema>;

const TurnClaudeSystemBaseSchema = z.object({
  type: z.literal('system'),
  session_id: z.string(),
});

export const TurnClaudeSystemPayloadSchema = z.discriminatedUnion('subtype', [
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('init'),
    cwd: z.string().optional(),
    model: z.string().optional(),
    claude_code_version: z.string().optional(),
    permissionMode: z.string().optional(),
    apiKeySource: z.string().optional(),
    fast_mode_state: z.string().optional(),
    output_style: z.string().optional(),
    tools: z.array(z.string()).optional(),
    agents: z.array(z.string()).optional(),
    skills: z.array(z.string()).optional(),
    slash_commands: z.array(z.string()).optional(),
    mcp_servers: z.array(z.unknown()).optional(),
    plugins: z.array(z.unknown()).optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('status'),
    status: z.string(),
    uuid: z.string().optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('compact_boundary'),
    compact_metadata: z.unknown().optional(),
    uuid: z.string().optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('task_started'),
    task_id: z.string(),
    task_type: z.enum(['local_agent', 'local_bash']),
    tool_use_id: z.string().optional(),
    description: z.string().optional(),
    uuid: z.string().optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('task_progress'),
    task_id: z.string(),
    tool_use_id: z.string().optional(),
    description: z.string().optional(),
    last_tool_name: z.string().optional(),
    usage: z.record(z.string(), z.unknown()).optional(),
    uuid: z.string().optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('task_notification'),
    task_id: z.string(),
    tool_use_id: z.string().optional(),
    status: z.enum(['completed', 'failed', 'stopped']),
    summary: z.string().optional(),
    output_file: z.string().optional(),
    usage: z.record(z.string(), z.unknown()).optional(),
    uuid: z.string().optional(),
  }),
  TurnClaudeSystemBaseSchema.extend({
    subtype: z.literal('task_updated'),
    task_id: z.string(),
    patch: z.object({
      end_time: z.number().optional(),
      status: z.enum(['completed', 'failed', 'stopped']).optional(),
    }),
    uuid: z.string().optional(),
  }),
]);
export type TurnClaudeSystemPayload = z.infer<typeof TurnClaudeSystemPayloadSchema>;

export const TurnClaudeErrorPayloadSchema = z.object({
  type: z.literal('error'),
  error: z.object({
    type: z.string(),
    message: z.string(),
  }),
  request_id: z.string().nullable().optional(),
  session_id: z.string().optional(),
});
export type TurnClaudeErrorPayload = z.infer<typeof TurnClaudeErrorPayloadSchema>;

export const TurnClaudeUserPayloadSchema = z.object({
  type: z.literal('user'),
  message: z.record(z.string(), z.unknown()),
  session_id: z.string(),
});
export type TurnClaudeUserPayload = z.infer<typeof TurnClaudeUserPayloadSchema>;

export const TurnClaudeRateLimitEventPayloadSchema = z.object({
  type: z.literal('rate_limit_event'),
  rate_limit_info: z.object({
    status: z.enum(['allowed', 'allowed_warning', 'rejected']),
    resetsAt: z.number().nullable().optional(),
    rateLimitType: z.string().optional(),
    utilization: z.number().optional(),
    isUsingOverage: z.boolean().optional(),
  }),
  session_id: z.string(),
});
export type TurnClaudeRateLimitEventPayload = z.infer<typeof TurnClaudeRateLimitEventPayloadSchema>;

export const TurnClaudeControlRequestPayloadSchema = z.object({
  type: z.literal('control_request'),
  request_id: z.string(),
  request: z.record(z.string(), z.unknown()),
});
export type TurnClaudeControlRequestPayload = z.infer<typeof TurnClaudeControlRequestPayloadSchema>;

export const TurnClaudeControlResponsePayloadSchema = z.object({
  type: z.literal('control_response'),
  response: z.record(z.string(), z.unknown()),
});
export type TurnClaudeControlResponsePayload = z.infer<
  typeof TurnClaudeControlResponsePayloadSchema
>;
